import type {
  ListSort,
  PostListPage,
  PostSearchPage,
  PostSearchParams,
  PublicPostDetail,
} from '@/types/api'
import { requestData, type QueryParams } from './client'

/**
 * 公开检索与详情。openapi.yaml tags: public。
 *
 * 三条容易踩的规则：
 *   - **未知查询参数一律 422**，所以序列化时绝不能带上 `undefined`，
 *     也不能自创参数（`search_mode` 会被明确拒绝：首期没有语义搜索）。
 *   - `sort=relevance` 要求同时提供 `q`，否则 422。这里在发请求前就拦住，
 *     省一次必然失败的往返。
 *   - `update_year` 是**过滤器而不是条件**：它无条件 AND，不参与 `match=any`，
 *     也不计入 `match.matched_count`。但它进入 cursor 指纹（后端指纹版本因此
 *     升到 v2），所以改年份必须回到第一页 —— 沿用旧 cursor 会 400。
 */

function toQuery(params: PostSearchParams): QueryParams {
  const query: QueryParams = {}
  if (params.q) query.q = params.q
  if (params.author_name?.length) query.author_name = params.author_name
  if (params.author_id?.length) query.author_id = params.author_id
  if (params.tag?.length) query.tag = params.tag
  if (params.tag_id?.length) query.tag_id = params.tag_id
  if (params.update_year) query.update_year = params.update_year
  if (params.match) query.match = params.match
  if (params.sort) query.sort = params.sort
  if (params.cursor) query.cursor = params.cursor
  if (params.limit !== undefined) query.limit = params.limit
  return query
}

export function normalizeSearchParams(params: PostSearchParams): PostSearchParams {
  // 没有 q 的 relevance 会被后端 422；缺省行为本来就是「有 q 用 relevance，无 q 用 latest」。
  if (params.sort === 'relevance' && !params.q) {
    const { sort: _sort, ...rest } = params
    return rest
  }
  return params
}

export function searchPosts(
  params: PostSearchParams,
  signal?: AbortSignal,
): Promise<PostSearchPage> {
  return requestData<PostSearchPage>('/posts', {
    query: toQuery(normalizeSearchParams(params)),
    ...(signal ? { signal } : {}),
  })
}

/** 已发布帖子详情。草稿、旧 sealed 修订、hidden / archived / 已删除一律 404。 */
export function fetchPostDetail(slug: string, signal?: AbortSignal): Promise<PublicPostDetail> {
  return requestData<PublicPostDetail>(`/posts/${encodeURIComponent(slug)}`, {
    ...(signal ? { signal } : {}),
  })
}

export interface ListPageParams {
  cursor?: string
  limit?: number
  sort?: ListSort
}

function toListQuery(params: ListPageParams): QueryParams {
  const query: QueryParams = {}
  if (params.cursor) query.cursor = params.cursor
  if (params.limit !== undefined) query.limit = params.limit
  if (params.sort) query.sort = params.sort
  return query
}

export function fetchCreatorPosts(
  publicId: string,
  params: ListPageParams = {},
  signal?: AbortSignal,
): Promise<PostListPage> {
  return requestData<PostListPage>(`/creators/${encodeURIComponent(publicId)}/posts`, {
    query: toListQuery(params),
    ...(signal ? { signal } : {}),
  })
}

export function fetchTagPosts(
  publicId: string,
  params: ListPageParams = {},
  signal?: AbortSignal,
): Promise<PostListPage> {
  return requestData<PostListPage>(`/tags/${encodeURIComponent(publicId)}/posts`, {
    query: toListQuery(params),
    ...(signal ? { signal } : {}),
  })
}
